import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Table,
  Space,
  message,
  Select,
  InputNumber,
  Input,
  DatePicker,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import {
  createWorkingSchedule,
  getWorkingSchedules,
  updateWorkingSchedule,
} from "../../../api/schedule/workingScheduleRequest";
import {
  createWorkingPeriod,
  getWorkingPeriods,
  updateWorkingPeriod,
} from "../../../api/schedule/workingPeriodRequest";
import FormModal from "../../../components/FormModal";
const { RangePicker } = DatePicker;

const getWorkingNamebyId = (id, periods) => {
  const period = periods.find((p) => p.id === id);
  return period ? period.name : "";
};

function WorkingHoursTab() {
  const [workingPeriods, setWorkingPeriods] = useState([]);
  const [workingSchedules, setWorkingSchedules] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [periods, schedules] = await Promise.all([
        getWorkingPeriods(),
        getWorkingSchedules(),
      ]);
      setWorkingPeriods(periods);
      setWorkingSchedules(schedules);
    } catch (error) {
      message.error("Erreur de chargement des horaires");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (modalType === "period") {
        if (
          !formData.dates ||
          formData.dates.length !== 2 ||
          !formData.priority
        ) {
          message.error("Veuillez saisir une date de début et de fin");
          return;
        }
        const periodData = {
          ...formData,
          start_date: dayjs(formData.dates[0]).format("MM-DD"),
          end_date: dayjs(formData.dates[1]).format("MM-DD"),
        };

        if (editingId) {
          await updateWorkingPeriod(editingId, periodData);
          message.success("Période de travail mise à jour");
        } else {
          await createWorkingPeriod(periodData);
          message.success("Période de travail ajoutée");
        }
      } else {
        if (!formData.period_id || !formData.start_time || !formData.end_time) {
          message.error(
            "Veuillez saisir une période de travail et des horaires"
          );
          return;
        }

        const scheduleData = {
          ...formData,
        };
        console.log("scheduleData", scheduleData);

        if (editingId) {
          await updateWorkingSchedule(editingId, formData);
          message.success("Horaire mis à jour");
        } else {
          await createWorkingSchedule(formData);
          message.success("Horaire ajouté");
        }
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      message.error("Erreur lors de l'enregistrement");
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setModalType(null);
    setFormData({});
    setEditingId(null);
  };

  const showModal = (type, data = {}) => {
    if (type === "period") {
      if (data?.start_date && data?.end_date)
        setFormData({
          ...data,
          dates: [dayjs(data.start_date), dayjs(data.end_date)],
        });
      else setFormData(data);
    } else {
      setFormData(data);
    }
    setModalType(type);
    setEditingId(data?.id || null);
    setIsModalVisible(true);
  };

  const periodColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
    },
    { title: "Période", dataIndex: "name" },
    {
      title: "Dates",
      render: (_, r) => `${r.start_date} - ${r.end_date}`,
    },
    {
      title: "Priorité",
      dataIndex: "priority",
      key: "priority",
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showModal("period", record)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  const scheduleColumns = [
    {
      title: "Période",
      dataIndex: "period_id",
      render: (text, record) =>
        getWorkingNamebyId(record.period_id, workingPeriods) || text,
      sorter: (a, b) => a.id - b.id,
    },
    { title: "Heure de début", dataIndex: "start_time" },
    { title: "Heure de fin", dataIndex: "end_time" },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showModal("schedule", record)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  const renderModalContent = () => {
    if (modalType === "period") {
      return (
        <>
          <Input
            placeholder="Nom de la période"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mb-3"
          />
          <RangePicker
            format="MM-DD"
            value={formData.dates?.map((d) => (d ? dayjs(d, "MM-DD") : null))}
            onChange={(dates) =>
              setFormData({
                ...formData,
                dates: dates ? dates.map((d) => d.format("MM-DD")) : null,
              })
            }
            className="w-full"
          />
          <InputNumber
            placeholder="Priorité"
            value={formData.priority}
            onChange={(v) => setFormData({ ...formData, priority: v })}
            className="w-full"
          />
        </>
      );
    }

    return (
      <>
        <Select
          placeholder="Période de travail"
          options={workingPeriods.map((p) => ({
            value: p.id,
            label: p.name,
          }))}
          value={formData.period_id}
          onChange={(v) => setFormData({ ...formData, period_id: v })}
          className="w-full mb-3"
          disabled={!!editingId}
        />
        <TimePicker.RangePicker
          format="HH:mm"
          value={[
            formData.start_time ? dayjs(formData.start_time, "HH:mm") : null,
            formData.end_time ? dayjs(formData.end_time, "HH:mm") : null,
          ]}
          onChange={(_, times) =>
            setFormData({
              ...formData,
              start_time: times[0],
              end_time: times[1],
            })
          }
          className="w-full"
        />
      </>
    );
  };

  return (
    <>
      <Card className="mb-6 shadow-sm">
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal("period")}
          >
            Nouvelle période
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => showModal("schedule")}>
            Ajouter un horaire
          </Button>
        </Space>
      </Card>

      <Table
        columns={periodColumns}
        dataSource={workingPeriods}
        loading={loading}
        rowKey="id"
        className="mb-6"
      />

      <Table
        columns={scheduleColumns}
        dataSource={workingSchedules}
        loading={loading}
        rowKey="id"
      />

      <FormModal
        title={editingId ? "Modification" : "Nouvelle entrée"}
        visible={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
      >
        {renderModalContent()}
      </FormModal>
    </>
  );
}

export default WorkingHoursTab;
